import { sanitizeDestFolder } from '@xphoto/shared';

/**
 * Préférence UTILISATEUR : DERNIER dossier de destination du téléversement (XPHOTO-16),
 * stockée CÔTÉ CLIENT en `localStorage` — même patron que la préférence de tri
 * (`default-sort-pref.ts`) et le thème, sans endpoint serveur.
 *
 * La modale de téléversement suggère par défaut : le dossier OUVERT dans la galerie
 * s'il y en a un ; sinon CE dernier dossier utilisé ; sinon la racine. On persiste le
 * dossier choisi APRÈS un téléversement réussi.
 *
 * PUR & tolérant si `localStorage` est indisponible (mode privé strict/quotas) : repli
 * en mémoire. Une valeur lue est RE-VALIDÉE par `sanitizeDestFolder` (jamais un préfixe
 * hostile hérité du stockage).
 */

/** Clé de persistance (namespace `xphoto.*`). */
export const UPLOAD_LAST_FOLDER_KEY = 'xphoto.upload.lastFolder';

/** Repli mémoire quand `localStorage` est inaccessible (jamais d'exception). */
let memoryFolder: string | null = null;

/**
 * Lit le dernier dossier persisté, RE-NORMALISÉ (préfixe POSIX sûr), ou `null` si
 * absent/invalide. La racine (`''`) est une valeur valide et distincte de l'absence.
 */
export function readLastUploadFolder(): string | null {
  let raw: string | null;
  try {
    raw =
      typeof localStorage === 'undefined'
        ? memoryFolder
        : localStorage.getItem(UPLOAD_LAST_FOLDER_KEY);
  } catch {
    raw = memoryFolder;
  }
  if (raw === null) return null;
  if (raw === '') return ''; // racine explicitement mémorisée
  return sanitizeDestFolder(raw); // re-valide → null si hostile
}

/**
 * Persiste le dernier dossier utilisé (repli mémoire + `localStorage`, silencieux si
 * indisponible). La racine (`''`) est acceptée ; toute autre valeur est normalisée et
 * ignorée si invalide (garde-fou).
 */
export function storeLastUploadFolder(folder: string): void {
  const value = folder === '' ? '' : sanitizeDestFolder(folder);
  if (value === null) return;
  memoryFolder = value;
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(UPLOAD_LAST_FOLDER_KEY, value);
  } catch {
    /* stockage indisponible : la préférence mémoire fait foi pour la session. */
  }
}
